import React, { createContext, useCallback, useMemo, useState } from 'react';
import { postBulkFilter } from 'api/dashboard';

function collectIdPairsFromConfig(config) {
    const sections = Array.isArray(config?.sections) ? config.sections : [];
    const seen = new Set();
    const pairs = [];
    for (let i = 0; i < sections.length; i++) {
        const apiParams = sections[i]?.props?.apiParams;
        const dashboardId = apiParams?.dashboard_id;
        const chartid = apiParams?.chartid;
        if (dashboardId == null || chartid == null) continue;
        const key = `${dashboardId}:${chartid}`;
        if (seen.has(key)) continue;
        seen.add(key);
        pairs.push({ key, dashboard_id: dashboardId, chartid });
    }
    return pairs;
}

export const BhimaBulkDataContext = createContext({
    loading: true,
    error: null,
    dataByKey: {},
    refetch: () => {}
});

const DEFAULT_FILTER_PAYLOAD = {
    filter: {
        dateFilter: { startDate: '', endDate: '' },
        brachFlter: '',
        scheme_type: []
    }
};

export function BhimaBulkDataProvider({ config, filter, children }) {
    const payloadFilter = filter ?? DEFAULT_FILTER_PAYLOAD;
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dataByKey, setDataByKey] = useState({});

    const idList = useMemo(() => {
        const pairs = collectIdPairsFromConfig(config);
        return pairs.map(({ dashboard_id, chartid }) => ({ chat_id: chartid, dashboard_id }));
    }, [config]);

    const fetchBulk = useCallback(() => {
        if (idList.length === 0) {
            setDataByKey({});
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        postBulkFilter({ ...payloadFilter, id: idList })
            .then((response) => {
                const list = response?.results ?? response?.data ?? (Array.isArray(response) ? response : []);
                const nextByKey = {};
                if (Array.isArray(list)) {
                    list.forEach((item) => {
                        if (!item || typeof item !== 'object') return;
                        const dashboardId = item.dashboard_id;
                        const chartid = item.chartid ?? item.chat_id;
                        if (dashboardId == null || chartid == null) return;
                        const key = `${dashboardId}:${chartid}`;
                        nextByKey[key] = { results: [item] };
                    });
                }
                setDataByKey(nextByKey);
            })
            .catch((err) => {
                setError(err?.message ?? err);
                setDataByKey({});
            })
            .finally(() => setLoading(false));
    }, [config, payloadFilter, idList]);

    React.useEffect(() => {
        fetchBulk();
    }, [fetchBulk]);

    const value = useMemo(() => ({
        loading,
        error,
        dataByKey,
        refetch: fetchBulk
    }), [loading, error, dataByKey, fetchBulk]);

    return (
        <BhimaBulkDataContext.Provider value={value}>
            {children}
        </BhimaBulkDataContext.Provider>
    );
}

export function useBhimaBulkData() {
    return React.useContext(BhimaBulkDataContext);
}
